import _ from "lodash"

import {
  getSectorDrawingValuesForPivotActivity,
  isActivityFinished,
} from "@/base/device-activity-utils"
import * as Geo from "@/base/geo"
import { isCenterPivotInstallationType } from "@/base/sensor-configurations"
import type { CenterPivot } from "@/redux/center-pivots.reducer"
import * as Sentry from "@sentry/react-native"
import * as turf from "@turf/turf"

import type { PivotDirection } from "@/constants/base"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import type * as Models from "@/redux/models"

/**
 * Pivot geometry lives on `CenterPivot` (migration 0266). Device config
 * fallbacks were removed; pass a CenterPivot entity to any helper that needs
 * geometry.
 */

/**
 * Determine if a device should show the pivot outline based on installation
 * type and association with a CenterPivot entity.
 *
 * Rules:
 * - center_pivot_controller always shows outline
 * - center_pivot_monitor only shows outline if no controller exists for same pivot
 */
export function shouldShowPivotOutline(
  configuration: DeviceConfiguration,
  centerPivot?: CenterPivot | null,
): boolean {
  const installationType = configuration.deviceInstallationType

  if (installationType === "center_pivot_controller") return true

  if (installationType === "center_pivot_monitor") {
    return centerPivot != null && !centerPivot.controllerDeviceId
  }

  return false
}

export const CENTER_PIVOT_ARROW_PARAMS = {
  baseLengthMeters: 200,
  headAngle: 150,
  headSizeMeters: 30,
}

export function makePlotArrow<P extends Geo.AnyLatLng>(
  transformPoint: Geo.PointTransformer<P>,
) {
  return (
    deviceActivity: Models.DeviceActivity,
    _configuration: DeviceConfiguration,
    centerPivot?: CenterPivot | null,
  ) => {
    const deviceHeading = _.findLast(
      deviceActivity.gpsHeadingsDegrees,
      (heading) => typeof heading === "number",
    )
    const arrowStemPath: P[] = []
    const arrowHeadPath: P[] = []
    if (typeof deviceHeading === "number") {
      // Get pivot configuration from CenterPivot entity or device configuration
      const pivotConfig = getPivotConfig(configuration, centerPivot)

      // Find the center of the pivot frontMidpoint
      let frontMidpoint: Geo.PointInput | undefined
      const currentAzimuth =
        deviceActivity.pivotHeadingsDegrees?.[
          deviceActivity.pivotHeadingsDegrees.length - 1
        ]

      if (typeof currentAzimuth === "number") {
        const pivotCenter = centerPivot?.pivotCenterGpsLocation
        const pivotRadius = centerPivot?.pivotRadiusMeters

        if (typeof pivotRadius === "number" && pivotCenter) {
          frontMidpoint = Geo.point(pivotCenter)?.project({
            direction: currentAzimuth,
            distanceMeters: pivotRadius / 2,
          })
        }
      }

      const arrowStart = transformPoint(frontMidpoint)

      const arrowEnd = Geo.point(arrowStart)?.project({
        direction: deviceHeading,
        distanceMeters: CENTER_PIVOT_ARROW_PARAMS.baseLengthMeters / 2,
      })

      const arrowEndLatLng = transformPoint(arrowEnd)

      if (arrowStart && arrowEnd && arrowEndLatLng) {
        arrowStemPath.push(arrowStart, arrowEndLatLng)

        // Add arrowhead

        const arrowHeadLeft = transformPoint(
          arrowEnd.project({
            direction: deviceHeading + CENTER_PIVOT_ARROW_PARAMS.headAngle,
            distanceMeters: CENTER_PIVOT_ARROW_PARAMS.headSizeMeters,
          }),
        )

        const arrowHeadRight = transformPoint(
          arrowEnd.project({
            direction: deviceHeading - CENTER_PIVOT_ARROW_PARAMS.headAngle,
            distanceMeters: CENTER_PIVOT_ARROW_PARAMS.headSizeMeters,
          }),
        )
        if (arrowHeadLeft && arrowHeadRight) {
          arrowHeadPath.push(arrowHeadLeft, arrowEndLatLng, arrowHeadRight)
        }
      }
    }

    return { arrowHeadPath, arrowStemPath }
  }
}

/**
 * Factory to find the sector (pie wedge) and transform it into a path that
 * we can render using one of our mapping libraries.
 *
 * When BOTH azimuthStart and azimuthEnd are null/undefined the sector is
 * treated as a full circle and rendered via ``turf.circle`` — this avoids
 * the prior convention of passing [0, 360] as a sentinel for "draw the
 * full sweep". When both are numbers, the result is a partial-arc sector
 * traced clockwise (or counter-clockwise) from start to end.
 */
export function makeSectorFinder<P extends Geo.AnyLatLng>(
  transformPoint: Geo.PointTransformer<P>,
) {
  return ({
    azimuthEnd,
    azimuthStart,
    centerCoordinates,
    direction,
    radiusMeters,
  }: {
    azimuthEnd: number | null | undefined
    azimuthStart: number | null | undefined
    centerCoordinates: Geo.Coordinates | null | undefined
    direction: PivotDirection
    radiusMeters: number | null | undefined
  }) => {
    if (
      !centerCoordinates ||
      typeof radiusMeters !== "number" ||
      // turf.sector / turf.circle throw on radius = 0.
      !radiusMeters
    ) {
      return undefined
    }

    const transformPathCoordinates = (
      pathCoordinates: number[][],
    ): P[] => {
      const result: P[] = []
      for (const coordinate of pathCoordinates) {
        const transformed = transformPoint(coordinate)
        if (transformed) {
          result.push(transformed)
        }
      }
      return result
    }

    // Full circle: both endpoints null = "no boundary defined". Render the
    // closed ring via turf.circle.
    if (azimuthStart == null && azimuthEnd == null) {
      try {
        const pivotPolygon = turf.circle(
          centerCoordinates,
          radiusMeters,
          { units: "meters" },
        )
        const pathCoordinates = pivotPolygon.geometry.coordinates[0] ?? []
        return transformPathCoordinates(pathCoordinates)
      } catch (e) {
        Sentry.captureException(e)
        return undefined
      }
    }

    // Partial arc: both endpoints must be numbers; mixed null/number is
    // malformed and renders nothing.
    if (
      typeof azimuthStart === "number" &&
      typeof azimuthEnd === "number"
    ) {
      try {
        let startAngle = azimuthStart
        let endAngle = azimuthEnd

        if (direction === "pivot_counterclockwise") {
          startAngle = azimuthEnd
          endAngle = azimuthStart
        }

        const pivotPolygon = turf.sector(
          centerCoordinates,
          radiusMeters,
          startAngle,
          endAngle,
          { units: "meters" },
        )
        const pathCoordinates = pivotPolygon.geometry.coordinates[0] ?? []
        return transformPathCoordinates(pathCoordinates)
      } catch (e) {
        Sentry.captureException(e)
      }
    }
    return undefined
  }
}

export function makePlotPivotOutline<P extends Geo.AnyLatLng>(
  transformPoint: Geo.PointTransformer<P>,
) {
  return (configuration: DeviceConfiguration, centerPivot?: CenterPivot | null) => {
    let extentPath: P[] | undefined

    // Check if this is any center pivot installation type
    if (!isCenterPivotInstallationType(configuration.deviceInstallationType)) {
      return extentPath
    }

    // Check if this device should show the pivot outline
    if (!shouldShowPivotOutline(configuration, centerPivot)) {
      return extentPath
    }

    // Geometry from the linked CenterPivot. boundary === null means
    // full-circle pivot (getSector handles via turf.circle internally).
    if (
      centerPivot != null &&
      typeof centerPivot.pivotRadiusMeters === "number" &&
      centerPivot.pivotCenterGpsLocation
    ) {
      const getSector = makeSectorFinder(transformPoint)
      extentPath = getSector({
        azimuthEnd: centerPivot.boundary?.endDegrees ?? null,
        azimuthStart: centerPivot.boundary?.startDegrees ?? null,
        centerCoordinates: centerPivot.pivotCenterGpsLocation.coordinates,
        direction: "pivot_clockwise",
        radiusMeters: centerPivot.pivotRadiusMeters,
      })
    }
    return extentPath
  }
}

export function makePlotPivotPath<P extends Geo.AnyLatLng>(
  transformPoint: Geo.PointTransformer<P>,
) {
  const getSector = makeSectorFinder(transformPoint)
  return (
    configuration: DeviceConfiguration,
    deviceActivity: Models.DeviceActivity,
    centerPivot?: CenterPivot | null,
  ) => {
    let activityPath: P[] | undefined

    // Check if this is any center pivot installation type
    if (!isCenterPivotInstallationType(configuration.deviceInstallationType)) {
      return activityPath
    }

    if (
      centerPivot != null &&
      typeof centerPivot.pivotRadiusMeters === "number" &&
      // If the pivot radius is 0, turf.sector throws an error 🙄
      centerPivot.pivotRadiusMeters > 0 &&
      centerPivot.pivotCenterGpsLocation &&
      !isActivityFinished(deviceActivity)
    ) {
      const sectorValues =
        getSectorDrawingValuesForPivotActivity(deviceActivity)
      if (sectorValues) {
        activityPath = getSector({
          azimuthEnd: sectorValues.mostRecentValidAzimuth,
          azimuthStart: sectorValues.activityStartAzimuth,
          centerCoordinates: centerPivot.pivotCenterGpsLocation.coordinates,
          direction: sectorValues.currentDirection,
          radiusMeters: centerPivot.pivotRadiusMeters,
        })
      }
    }
    return activityPath
  }
}

// export const CenterPivotColors = {
//   outline: {
//     strokeColor: COLORS.$cyan[500],
//   },
// }
